import { useId, useState } from "react";
import styled from "styled-components";
import { FaChevronDown, FaClock, FaRegFileAlt, FaStop, FaSyncAlt } from "react-icons/fa";

export function getLocalOcrQueueMessage(job, fallback = "後端 OCR 工作排隊中…") {
  const ahead = job.queue_ahead_count;
  if (typeof ahead !== "number") return fallback;
  if (ahead > 0) return `前方還有 ${ahead} 筆工作，等待輪到這份檔案…`;
  if (job.running_job_count === 0) {
    return Number(job.queue_wait_seconds) > 20
      ? "等待 OCR 主機領取…等候較久，可先重新整理工作；若持續等待，請檢查 OCR 主機服務。"
      : "等待 OCR 主機領取…";
  }
  return fallback;
}

const Panel = styled.section`
  margin-top: 12px;
  color: var(--app-surface-text, #242424);
  font-size: 13px;
  line-height: 1.5;
  button { font: inherit; cursor: pointer; }
  button:disabled { cursor: wait; opacity: 0.6; }
  button:focus-visible { outline: 2px solid var(--app-icon-accent, #2563eb); outline-offset: 3px; }
`;
const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 34px;
  color: var(--app-muted-text, #6b7280);
  strong { color: var(--app-surface-text, #242424); font-weight: 750; }
`;
const RefreshButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
  border: 1px solid transparent;
  border-radius: 7px;
  color: var(--app-muted-text, #6b7280);
  background: transparent;
  &:hover { color: var(--app-surface-text, #242424); background: var(--app-surface-soft, #f4f4f4); border-color: var(--app-border, #d8d8d8); }
`;
const ActiveCard = styled.div`
  display: grid;
  gap: 10px;
  margin-top: 5px;
  padding: 12px;
  border: 1px solid var(--app-border, #d8d8d8);
  border-radius: 8px;
  background: var(--app-input-bg, #fff);
`;
const StatusLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 6px;
  color: var(--app-muted-text, #6b7280);
  font-size: 12px;
`;
const StatusLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 5px;
  padding: 3px 7px;
  color: var(--app-surface-text, #303134);
  background: var(--app-surface-soft, #f4f4f4);
  font-weight: 700;
`;
const Filename = styled.strong`
  display: block;
  min-width: 0;
  overflow-wrap: anywhere;
  font-size: 13px;
  line-height: 1.5;
`;
const Detail = styled.p`
  margin: 0;
  color: var(--app-muted-text, #6b7280);
  font-size: 12px;
  overflow-wrap: anywhere;
`;
const StopButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  width: 100%;
  min-height: 38px;
  padding: 8px 12px;
  border: 1px solid var(--app-danger-border, #e7b6b0);
  border-radius: 7px;
  color: var(--app-danger-text, #b3261e);
  background: var(--app-danger-bg, #fff7f5);
  font-weight: 750 !important;
  &:hover:not(:disabled) { border-color: currentColor; }
`;
const ErrorText = styled.p`
  margin: 6px 0 0;
  color: var(--app-danger-text, #b3261e);
  font-size: 12px;
  overflow-wrap: anywhere;
`;
const Notice = styled.p`
  margin: 6px 0 0;
  color: var(--app-muted-text, #6b7280);
  font-size: 12px;
  overflow-wrap: anywhere;
`;
const Disclosure = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  min-height: 38px;
  margin-top: 7px;
  padding: 7px 2px;
  border: 0;
  border-top: 1px solid var(--app-border, #d8d8d8);
  color: var(--app-muted-text, #6b7280);
  background: transparent;
  text-align: left;
  svg { transition: transform 0.15s; transform: rotate(${({ $expanded }) => $expanded ? "180deg" : "0deg"}); }
  @media (prefers-reduced-motion: reduce) { svg { transition: none; } }
`;
const Queue = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;
const QueueRow = styled.li`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: start;
  gap: 8px;
  padding: 10px 0;
  border-top: 1px solid var(--app-border, #d8d8d8);
  > div { min-width: 0; }
  @media (max-width: 380px) { grid-template-columns: minmax(0, 1fr); }
`;
const QueueCancel = styled.button`
  min-height: 34px;
  padding: 5px 9px;
  border: 1px solid var(--app-border, #d8d8d8);
  border-radius: 6px;
  color: var(--app-subtle-text, #4f4f4f);
  background: var(--app-surface, #fbfbfb);
  &:hover:not(:disabled) { color: var(--app-danger-text, #b3261e); }
  @media (max-width: 380px) { justify-self: end; }
`;

const pagesLabel = (job) => Number(job?.total) > 0
  ? `已完成 ${Number(job.current) || 0} / ${Number(job.total)} 頁` : "正在辨識文字";

const elapsedLabel = (seconds) => {
  const value = Math.max(0, Math.floor(Number(seconds) || 0));
  if (!value) return "";
  return value < 60 ? `${value} 秒` : `${Math.floor(value / 60)} 分${value % 60 ? ` ${value % 60} 秒` : ""}`;
};

export default function LocalOcrJobControls({ controls, elapsedSeconds = 0 }) {
  const { canStop, stopping, stop, stopError, jobs, refresh, listError, notice,
    currentJobId, currentJob, cancelQueuedJob, isCancelling, cancelErrors, retryRecovery, recoveryError } = controls;
  const [expanded, setExpanded] = useState(false);
  const queueId = useId();
  const otherJobs = jobs.filter((job) => job.id !== currentJobId);
  const state = currentJob?.status || "creating";
  const currentState = stopping ? "正在停止" : state === "running" ? "解析中" : state === "queued" ? "排隊中" : "正在送出";
  const currentDetail = state === "queued" ? getLocalOcrQueueMessage(currentJob)
    : state === "running" ? pagesLabel(currentJob) : "正在上傳並建立工作…";
  const elapsed = elapsedLabel(elapsedSeconds);
  const hasWork = canStop || otherJobs.length > 0;
  return <Panel aria-label="目前 OCR 工作">
    <Header>
      <span><strong>OCR 工作</strong>{!hasWork && !listError && !recoveryError && <span> · 目前閒置</span>}</span>
      <RefreshButton type="button" title="重新整理工作" aria-label="重新整理工作"
        onClick={() => { refresh(); retryRecovery(); }}><FaSyncAlt aria-hidden="true" /></RefreshButton>
    </Header>
    {canStop && <ActiveCard aria-label="目前解析任務">
      <StatusLine><StatusLabel>{state === "queued" ? <FaClock aria-hidden="true" /> : <FaRegFileAlt aria-hidden="true" />}{currentState}</StatusLabel><span>{elapsed ? `${state === "running" ? "解析" : "等候"} ${elapsed}` : "目前任務"}</span></StatusLine>
      <Filename title={currentJob?.filename}>{currentJob?.filename || "正在送出的檔案"}</Filename>
      <Detail>{currentDetail}</Detail>
      <StopButton type="button" disabled={stopping} onClick={stop}>
        <FaStop aria-hidden="true" />{stopping ? "正在停止…" : "停止解析"}
      </StopButton>
      {stopError && <ErrorText role="alert">{stopError}</ErrorText>}
      <Detail>重整或離開本頁時，會通知後端停止這份工作。</Detail>
    </ActiveCard>}
    {listError && <ErrorText role="alert">{listError}</ErrorText>}
    {recoveryError && <ErrorText role="alert">{recoveryError}</ErrorText>}
    {notice && <Notice role="status">{notice}</Notice>}
    {otherJobs.length > 0 && <>
      <Disclosure type="button" aria-expanded={expanded} aria-controls={queueId} $expanded={expanded}
        aria-label={`${expanded ? "收合" : "查看"}${canStop ? "其他" : "全部"} OCR 工作（${otherJobs.length}）`}
        onClick={() => setExpanded((value) => !value)}>
        <span>{canStop ? "其他工作" : "排隊與處理中的工作"} · {otherJobs.length}</span><FaChevronDown aria-hidden="true" />
      </Disclosure>
      {expanded && <Queue id={queueId}>
        {otherJobs.map((job) => <QueueRow key={job.id}>
          <div><Filename title={job.filename}>{job.filename || "未命名檔案"}</Filename>
            <Detail>{job.status === "queued" ? getLocalOcrQueueMessage(job, "排隊中") : pagesLabel(job)}</Detail>
            {cancelErrors[job.id] && <ErrorText role="alert">{cancelErrors[job.id]}</ErrorText>}
          </div>
          <QueueCancel type="button" disabled={isCancelling(job.id)}
            aria-label={`取消 ${job.filename || "未命名檔案"}`} onClick={() => cancelQueuedJob(job)}>
            {isCancelling(job.id) ? "正在停止…" : "取消"}
          </QueueCancel>
        </QueueRow>)}
      </Queue>}
    </>}
  </Panel>;
}
